/**
 * Etiket tasarımcısı araç çubuğu: eleman ekleme, hizalama, katman sırası,
 * yakınlaştırma ve görünüm anahtarları.
 */

import React from 'react';
import {
  Box,
  Button,
  Divider,
  IconButton,
  Slider,
  Stack,
  ToggleButton,
  Tooltip,
  Typography,
} from '@mui/material';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import ViewWeekIcon from '@mui/icons-material/ViewWeek';
import ImageIcon from '@mui/icons-material/Image';
import CropSquareIcon from '@mui/icons-material/CropSquare';
import HorizontalRuleIcon from '@mui/icons-material/HorizontalRule';
import GridOnIcon from '@mui/icons-material/GridOn';
import CodeIcon from '@mui/icons-material/Code';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import SaveIcon from '@mui/icons-material/Save';
import PrintIcon from '@mui/icons-material/Print';
import AlignHorizontalLeftIcon from '@mui/icons-material/AlignHorizontalLeft';
import AlignHorizontalCenterIcon from '@mui/icons-material/AlignHorizontalCenter';
import AlignHorizontalRightIcon from '@mui/icons-material/AlignHorizontalRight';
import AlignVerticalCenterIcon from '@mui/icons-material/AlignVerticalCenter';
import FlipToFrontIcon from '@mui/icons-material/FlipToFront';
import FlipToBackIcon from '@mui/icons-material/FlipToBack';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import MagnetIcon from '@mui/icons-material/Straighten';
import VerifiedIcon from '@mui/icons-material/Verified';
import { LabelElementType } from '../../../types/labelTemplate.types';

export type AlignAction = 'left' | 'centerH' | 'right' | 'centerV';
export type LayerAction = 'front' | 'back';

export interface LabelToolbarProps {
  onAddElement: (type: LabelElementType) => void;
  onAlign: (action: AlignAction) => void;
  onLayer: (action: LayerAction) => void;
  hasSelection: boolean;

  zoom: number;
  onZoomChange: (z: number) => void;

  showGrid: boolean;
  onToggleGrid: () => void;
  snapEnabled: boolean;
  onToggleSnap: () => void;
  showRawPlaceholders: boolean;
  onToggleRawPlaceholders: () => void;

  canUndo: boolean;
  canRedo: boolean;
  onUndo: () => void;
  onRedo: () => void;

  onSave: () => void;
  onPrint: () => void;
  saving?: boolean;

  /** Yerli malı rozeti — koşulu hazır kurulmuş görsel eklenir */
  onAddDomesticBadge?: () => void;
}

const ADD_BUTTONS: Array<{ type: LabelElementType; label: string; icon: React.ReactElement }> = [
  { type: 'text', label: 'Metin', icon: <TextFieldsIcon fontSize="small" /> },
  { type: 'barcode', label: 'Barkod', icon: <ViewWeekIcon fontSize="small" /> },
  { type: 'qr', label: 'QR Kod', icon: <QrCode2Icon fontSize="small" /> },
  { type: 'image', label: 'Görsel', icon: <ImageIcon fontSize="small" /> },
  { type: 'rect', label: 'Kutu', icon: <CropSquareIcon fontSize="small" /> },
  { type: 'line', label: 'Çizgi', icon: <HorizontalRuleIcon fontSize="small" /> },
];

const LabelToolbar: React.FC<LabelToolbarProps> = ({
  onAddElement,
  onAlign,
  onLayer,
  hasSelection,
  zoom,
  onZoomChange,
  showGrid,
  onToggleGrid,
  snapEnabled,
  onToggleSnap,
  showRawPlaceholders,
  onToggleRawPlaceholders,
  canUndo,
  canRedo,
  onUndo,
  onRedo,
  onSave,
  onPrint,
  saving,
  onAddDomesticBadge,
}) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      px: 1.5,
      py: 1,
      borderBottom: '1px solid',
      borderColor: 'divider',
      flexWrap: 'wrap',
      bgcolor: 'background.paper',
    }}
  >
    {ADD_BUTTONS.map((b) => (
      <Tooltip key={b.type} title={`${b.label} ekle`}>
        <Button size="small" variant="outlined" startIcon={b.icon} onClick={() => onAddElement(b.type)} sx={{ textTransform: 'none' }}>
          {b.label}
        </Button>
      </Tooltip>
    ))}

    {onAddDomesticBadge && (
      <Tooltip title="Yerli malı rozeti ekle — sadece yerli malı işaretli ürünlerde basılır">
        <Button
          size="small"
          variant="outlined"
          startIcon={<VerifiedIcon fontSize="small" />}
          onClick={onAddDomesticBadge}
          sx={{ textTransform: 'none' }}
        >
          Yerli Malı
        </Button>
      </Tooltip>
    )}

    <Divider orientation="vertical" flexItem />

    <Tooltip title="Sola hizala">
      <IconButton size="small" disabled={!hasSelection} onClick={() => onAlign('left')}>
        <AlignHorizontalLeftIcon fontSize="small" />
      </IconButton>
    </Tooltip>
    <Tooltip title="Yatayda ortala">
      <IconButton size="small" disabled={!hasSelection} onClick={() => onAlign('centerH')}>
        <AlignHorizontalCenterIcon fontSize="small" />
      </IconButton>
    </Tooltip>
    <Tooltip title="Sağa hizala">
      <IconButton size="small" disabled={!hasSelection} onClick={() => onAlign('right')}>
        <AlignHorizontalRightIcon fontSize="small" />
      </IconButton>
    </Tooltip>
    <Tooltip title="Dikeyde ortala">
      <IconButton size="small" disabled={!hasSelection} onClick={() => onAlign('centerV')}>
        <AlignVerticalCenterIcon fontSize="small" />
      </IconButton>
    </Tooltip>

    <Divider orientation="vertical" flexItem />

    <Tooltip title="Öne getir">
      <IconButton size="small" disabled={!hasSelection} onClick={() => onLayer('front')}>
        <FlipToFrontIcon fontSize="small" />
      </IconButton>
    </Tooltip>
    <Tooltip title="Arkaya gönder">
      <IconButton size="small" disabled={!hasSelection} onClick={() => onLayer('back')}>
        <FlipToBackIcon fontSize="small" />
      </IconButton>
    </Tooltip>

    <Divider orientation="vertical" flexItem />

    <Tooltip title="Izgarayı göster">
      <ToggleButton value="grid" size="small" selected={showGrid} onChange={onToggleGrid}>
        <GridOnIcon fontSize="small" />
      </ToggleButton>
    </Tooltip>
    <Tooltip title="Izgaraya yapıştır (0.5 mm)">
      <ToggleButton value="snap" size="small" selected={snapEnabled} onChange={onToggleSnap}>
        <MagnetIcon fontSize="small" />
      </ToggleButton>
    </Tooltip>
    <Tooltip title={showRawPlaceholders ? 'Örnek veriyle göster' : 'Veri alanı adlarını göster'}>
      <ToggleButton value="raw" size="small" selected={showRawPlaceholders} onChange={onToggleRawPlaceholders}>
        <CodeIcon fontSize="small" />
      </ToggleButton>
    </Tooltip>

    <Divider orientation="vertical" flexItem />

    <Tooltip title="Geri al (Ctrl+Z)">
      <IconButton size="small" disabled={!canUndo} onClick={onUndo}>
        <UndoIcon fontSize="small" />
      </IconButton>
    </Tooltip>
    <Tooltip title="İleri al (Ctrl+Y)">
      <IconButton size="small" disabled={!canRedo} onClick={onRedo}>
        <RedoIcon fontSize="small" />
      </IconButton>
    </Tooltip>

    <Divider orientation="vertical" flexItem />

    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 160 }}>
      <IconButton size="small" onClick={() => onZoomChange(Math.max(1, zoom - 0.5))}>
        <ZoomOutIcon fontSize="small" />
      </IconButton>
      <Slider
        size="small"
        min={1}
        max={10}
        step={0.5}
        value={zoom}
        onChange={(_, v) => onZoomChange(v as number)}
        sx={{ width: 80 }}
      />
      <IconButton size="small" onClick={() => onZoomChange(Math.min(10, zoom + 0.5))}>
        <ZoomInIcon fontSize="small" />
      </IconButton>
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 34 }}>
        {Math.round(zoom * 100)}%
      </Typography>
    </Stack>

    <Box sx={{ flex: 1 }} />

    <Button size="small" variant="outlined" startIcon={<SaveIcon />} onClick={onSave} disabled={saving}>
      Kaydet
    </Button>
    <Button size="small" variant="contained" startIcon={<PrintIcon />} onClick={onPrint}>
      Yazdır
    </Button>
  </Box>
);

export default LabelToolbar;
